import React, { createContext, useEffect, useState, useRef } from "react";
import units from "../utils/units";
import { DEFAULT_VAL_GROUPS } from "../utils/constants";
import { getAllIDsInModels } from "../components/rightSide/parameters/parameterLogic";
import { safeSessionStorageSet } from "../utils/helpers";

export const DashboardContext = createContext();

const isProduction =
  window.location.hostname === "lab.speqqle.com" ||
  window.location.hostname === "app.speqqle.com" ||
  window.location.hostname === "test.speqqle.com";

const DashboardContextProvider = (props) => {
  //saving loaded files into context to not lose them on ToggleButton close
  const [fileLoadData, setFileLoadData] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileID, setFileID] = useState(1);
  // const [graphs, setGraphs] = useState([]);

  //setting default values of the ZeroCutParams
  const [zeroCutParams, setZeroCutParams] = useState({
    min: -10,
    max: 10,
    auto: true,
  });

  //setting the default defaultXAxisUnit to the first unit of the list of units imported from src/utils/units.js (should be cm-1)
  const [defaultXAxisUnit, setDefaultXAxisUnit] = useState(units[0]);

  //setting default state of the otherParams to false ("only fit stokes" checkbox)
  const [otherParams, setOtherParams] = useState({
    onlyPos: false,
    analysisChecked: false,
  });

  const [minDataRangeVal, setMinDataRangeVal] = useState("");
  const [maxDataRangeVal, setMaxDataRangeVal] = useState("");
  const [abbrDic, setAbbrDic] = useState({});
  const [namingDic, setNamingDic] = useState({});
  const [modelNextID, setModelNextID] = useState(1);
  const [modelData, setModelData] = useState([]);
  const [allLocalModels, setAllLocalModels] = useState([]);
  const [allLocalLineshapes, setAllLocalLineshapes] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [newWarningCount, setNewWarningCount] = useState(0);

  // This variable keeps an old model state before fit/autofit was done.
  // If user decides that fit/autofit was incorrect, he can the revert back to old
  // model state. This variable is needed for that.
  const [undoModels, setUndoModels] = useState([]);

  // With this variable tracking we will know when to clean up models from unused curves
 
  const [valueGroups, setValueGroups] = useState(DEFAULT_VAL_GROUPS);

  const [sectionsStatus, setSectionsStatus] = useState({
    paramOpen: false,
    fitOpen: false,
  });
  const [showLeftPanel, setShowLeftPanel] = useState(true);

  // We use different variables to track clicks that happen in the graph legend
  // because we have separate logic for clicks on models and files
  const [callFromGraph, setCallFromGraph] = useState(false);
  const [fileCallFromGraph, setFileCallFromGraph] = useState(false);

  const [filePresetsLoadingOptions, setfilePresetsLoadingOptions] = useState([
    {
      presetName: "Default",
      id: 0,
      presetOptions: {
        colCount: 2,
        xUnit: units[0],
        separator: "whitespace",
        autoRemove: true,
        manualLineStart: 0,
        columns: [
          "x",
          "y",
          "ignore",
          "ignore",
          "ignore",
          "ignore",
          "ignore",
          "ignore",
          "ignore",
          "ignore",
        ],
        shift: false,
        xShift: "none",
        yShift: "none",
        xShiftVal: "",
        yShiftVal: "",
      },
    },
  ]);

  const [selectedPresetLoadingOptions, setselectedPresetLoadingOptions] = useState(null);
  const [fileToChange, setFileToChange] = useState(null);
  const [expandedWindows, setExpandedWindows] = useState({});
  const [fitIteration, setFitIteration] = useState(null);
  const [loopIteration, setLoopIteration] = useState(null);
  // const [requestedVdf, setRequestedVdf] = useState([]);

  // Fit options:
  const [maxIteration, setMaxIteration] = useState(20);
  const [iterationRefreshCount, setIterationRefreshCount] = useState("");
  const [maxRunTime, setMaxRunTime] = useState(100);
  const [chiVal, setChiVal] = useState({
    n: 3,
    val: 0.001,
  });
  const [totalActiveParams, setTotalActiveParams] = useState(0);
  const [demoOpen, setDemoOpen] = useState(false);
  // Initialize tutorialOpen based on tutorial state in sessionStorage
  const [tutorialOpen, setTutorialOpen] = useState(() => {
    try {
      const savedTutorialState = sessionStorage.getItem('tutorialState');
      if (savedTutorialState) {
        const state = JSON.parse(savedTutorialState);
        // Open tutorial tab if tutorial was active (has modal open or is running)
        return (state.localTutorialModalOpen || state.runningTutorial > 0) && state.currentTutorial;
      }
    } catch (error) {
      console.warn('Failed to load tutorial state for tab initialization:', error);
    }
    return false;
  });
  const [dataOpen, setDataOpen] = useState(false);
  const [modelsOpen, setModelsOpen] = useState(false);
  const [parametersOpen, setParametersOpen] = useState(false);
  const [fitOpen, setFitOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [outputsOpen, setOutputsOpen] = useState(true);
  const [loopOpen, setLoopOpen] = useState(false);
  const [chi2ReqAllowed, setChi2ReqAllowed] = useState(true);
  const chi2QueRef = useRef({});
  const [gaugeDict, setGaugeDict] = useState({});
  const [addGraphModalOpen, setAddGraphModalOpen] = useState(false);
  const [loopModelFilesCustomName, setLoopModelFilesCustomName] = useState("");
  const [loopCurveFilesCustomName, setLoopCurveFilesCustomName] = useState("");
  const [listOfLoopModelsToSave, setListOfLoopModelsToSave] = useState([]);
  const [listOfLoopCurvesToSave, setListOfLoopCurvesToSave] = useState([]);
  const [trackedParameters, setTrackedParameters] = useState([]);
  const [pauseOnNextLoop, setPauseOnNextLoop] = useState(false);
  const [loopPaused, setLoopPaused] = useState(false);
  const [fitUndoAvailable, setFitUndoAvailable] = useState(false);
  const [stickyParamPosition, setStickyParamPosition] = useState({
    x: window.innerWidth - 380,
    y: window.innerHeight - 450,
  });
  const [subModelColTitles, setSubModelColTitles] = useState([]);
  const tempAdditionalColumnTitles = useRef([]);
  const colTitleAddDebounce = useRef(null);
  const [paramWindowPosAndSize, setParamWindowPosAndSize] = useState({});
  const [areParamsSorted, setAreParamsSorted] = useState(true);
  const [modelsZIndices, setModelsZIndices] = useState(() => {
    const listOfAllModelIDs = getAllIDsInModels(modelData);

    const initialDict = {};

    for (let i = 0; i < listOfAllModelIDs.length; i++) {
      const ID = listOfAllModelIDs[i];

      initialDict[ID] = 0;
    }

    return initialDict;
  });
  const [checkParamPositions, setCheckParamPositions] = useState(false);

  // DEMO DEMO DEMO VVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV

  const [button1Active, setButton1Active] = useState(false);
  const [button2Active, setButton2Active] = useState(false);
  const [button3Active, setButton3Active] = useState(false);

  // DEMO DEMO DEMO ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^

  useEffect(() => {
    const handleResize = () => {
      const newX = Math.max(0, window.innerWidth - 380);
      const newY = Math.max(0, window.innerHeight - 450);
      setStickyParamPosition({ x: newX, y: newY });
    };

    window.addEventListener("resize", handleResize);

    // Clean up the event listener on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Update the default preset's xUnit when defaultXAxisUnit changes
  useEffect(() => {
    setfilePresetsLoadingOptions((prevPresets) => {
      return prevPresets.map((preset) => {
        if (preset.id === 0) {
          // Update the default preset (id: 0) with the new defaultXAxisUnit
          return {
            ...preset,
            presetOptions: {
              ...preset.presetOptions,
              xUnit: defaultXAxisUnit,
            },
          };
        }
        return preset;
      });
    });
  }, [defaultXAxisUnit]);

  function addSubModTitle(title) {
    // Clear any existing timeout to prevent race conditions
    if (colTitleAddDebounce.current) {
      clearTimeout(colTitleAddDebounce.current);
    } // Add the new title to the temporary ref
    tempAdditionalColumnTitles.current = [
      ...tempAdditionalColumnTitles.current,
      title,
    ];
    const addTitlesToState = () => {
      // Capture the current titles in a local variable to prevent them being cleared too early
      const titlesToAdd = [...tempAdditionalColumnTitles.current];

      // Always use the updater pattern to get the latest state
      setSubModelColTitles((currentTitles) => {
        // Handle the case when currentTitles is undefined by defaulting to an empty array
        const safeCurrentTitles = currentTitles || [];

        // Create a combined array with both current titles and new titles
        const combinedArray = [...safeCurrentTitles, ...titlesToAdd];

        // Use Set to ensure uniqueness, then convert back to array
        const newList = [...new Set(combinedArray)];

        // Clear the temporary titles after we've used them
        tempAdditionalColumnTitles.current = [];

        if (newList === undefined) {
          return [];
        }
        return newList;
      });
    };

    // Set a new timeout
    colTitleAddDebounce.current = setTimeout(addTitlesToState, 10);
  }

  function checkIfSubModTitle(title) {
    if (
      subModelColTitles !== undefined &&
      subModelColTitles.length > 0 &&
      subModelColTitles.some((colTitle) => colTitle === title)
    ) {
      return true;
    }

    return false;
  }

  function setDashboardVariables(variables) {
    const {
      fileLoadData = [],
      zeroCutParams = { min: -10, max: 10, auto: true },
      defaultXAxisUnit = units[0],
      otherParams = { onlyPos: false, analysisChecked: false },
      modelData = [],
      modelNextID = 1,
      minDataRangeVal = "",
      maxDataRangeVal = "",
      abbrDic = {},
      namingDic = {},
      warnings = [],
      allLocalModels = [],
      allLocalLineshapes = [],
      valueGroups = DEFAULT_VAL_GROUPS,
      uploadedFiles = [],
      fileID = 1,
      newWarningCount = 0,
      filePresetsLoadingOptions = [{
        presetName: "Default",
        id: 0,
        presetOptions: {
          colCount: 2,
          xUnit: units[0],
          separator: "whitespace",
          autoRemove: true,
          manualLineStart: 0,
          columns: ["x", "y", "ignore", "ignore", "ignore", "ignore", "ignore", "ignore", "ignore", "ignore"],
          shift: false,
          xShift: "none",
          yShift: "none",
          xShiftVal: "",
          yShiftVal: "",
        },
      }],
      selectedPresetLoadingOptions = null,
      maxIteration = 20,
      iterationRefreshCount = "",
      maxRunTime = 100,
      chiVal = { n: 3, val: 0.001 },
      totalActiveParams = 0,
      gaugeDict = {},
      trackedParameters = [],
      subModelColTitles = [],
      paramWindowPosAndSize = {},
      listOfLoopModelsToSave = [],
      listOfLoopCurvesToSave = [],
    } = variables;

    let checkedFileID = fileID;

    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i];

      if (file.ID >= checkedFileID) {
        checkedFileID = file.ID + 1;
      }
    }

    if (!isProduction && checkedFileID !== fileID) {
      console.warn(
        "THERE IS A MISSMATCH OF ID IN SESSION FILE VERSUS TOP ID IN FILES. ORIGINAL ID IN SESSION: ",
        fileID
      );
    }

    setFileLoadData(fileLoadData);
    setUploadedFiles(uploadedFiles);
    setFileID(checkedFileID);
    setZeroCutParams(zeroCutParams);
    setDefaultXAxisUnit(defaultXAxisUnit);
    setOtherParams(otherParams);
    setMinDataRangeVal(minDataRangeVal);
    setMaxDataRangeVal(maxDataRangeVal);
    setAbbrDic(abbrDic);
    setNamingDic(namingDic);
    setModelNextID(modelNextID);
    setModelData(modelData);
    setAllLocalModels(allLocalModels);
    setAllLocalLineshapes(allLocalLineshapes);
    setWarnings(warnings);
    setNewWarningCount(newWarningCount);
    setValueGroups(valueGroups);
    setTrackedParameters(trackedParameters);
    setfilePresetsLoadingOptions(filePresetsLoadingOptions);
    setselectedPresetLoadingOptions(selectedPresetLoadingOptions);
    setMaxIteration(maxIteration);
    setIterationRefreshCount(iterationRefreshCount);
    setMaxRunTime(maxRunTime);
    setChiVal(chiVal);
    setTotalActiveParams(totalActiveParams);
    setGaugeDict(gaugeDict);
    setSubModelColTitles(subModelColTitles);
    setParamWindowPosAndSize(paramWindowPosAndSize);
    setListOfLoopModelsToSave(listOfLoopModelsToSave);
    setListOfLoopCurvesToSave(listOfLoopCurvesToSave);
  }

  const resetDashboardContext = () => {
    setFileLoadData([]);
    setUploadedFiles([]);
    setFileID(1);
    // setGraphs([]); // Uncomment if you need to reset this state as well

    setZeroCutParams({
      min: -10,
      max: 10,
      auto: true,
    });

    setDefaultXAxisUnit(units[0]);

    setOtherParams({
      onlyPos: false,
      analysisChecked: false,
    });

    setMinDataRangeVal("");
    setMaxDataRangeVal("");
    setAbbrDic({});
    setNamingDic({});
    setModelNextID(1);
    setModelData([]);
    setAllLocalModels([]);
    setAllLocalLineshapes([]);
    setWarnings([]);
    setNewWarningCount(0);
    setUndoModels([]);
   
    setValueGroups(DEFAULT_VAL_GROUPS);

    // DEMO states
    setButton1Active(false);
    setButton2Active(false);
    setButton3Active(false);

    setSectionsStatus({
      paramOpen: false,
      fitOpen: false,
    });
    setShowLeftPanel(true);
    setCallFromGraph(false);
    setFileCallFromGraph(false);
    setfilePresetsLoadingOptions([
      {
        presetName: "Default",
        id: 0,
        presetOptions: {
          colCount: 2,
          xUnit: units[0],
          separator: "whitespace",
          autoRemove: true,
          manualLineStart: 0,
          col1: "x",
          col2: "y",
          col3: "e",
        },
      },
    ]);
    setselectedPresetLoadingOptions(null);

    setMaxIteration(20);
    setIterationRefreshCount("");
    setMaxRunTime(100);
    setChiVal({
      n: 3,
      val: 0.001,
    });
    setFileToChange(null);
    setExpandedWindows({});
    setFitIteration(null);
    setTotalActiveParams(0);
    setGaugeDict({});
    setDemoOpen(false);
    setTutorialOpen(false);

    setDataOpen(false);
    setModelsOpen(false);
    setParametersOpen(false);
    setFitOpen(false);
    setResultsOpen(false);
    setOutputsOpen(true);
    setLoopOpen(false);
    setChi2ReqAllowed(true);
    chi2QueRef.current = {};
    setGaugeDict({});
    setAddGraphModalOpen(false);
    setLoopModelFilesCustomName("");
    setLoopCurveFilesCustomName("");
    setLoopIteration(null);
    setListOfLoopCurvesToSave([]);
    setListOfLoopModelsToSave([]);
    setTrackedParameters([]);
    setPauseOnNextLoop(false);
    setLoopPaused(false);
    setFitUndoAvailable(false);
    setSubModelColTitles([]);
    setParamWindowPosAndSize({});
    setAreParamsSorted(true);
    setModelsZIndices(() => {
      const listOfAllModelIDs = getAllIDsInModels(modelData);

      const initialDict = {};

      for (let i = 0; i < listOfAllModelIDs.length; i++) {
        const ID = listOfAllModelIDs[i];

        initialDict[ID] = 0;
      }

      return initialDict;
    });
  };

  // Function to sync UI state with what's actually stored in sessionStorage
  const syncWithSessionStorage = () => {
    try {
      // Helper function to safely parse JSON from sessionStorage
      const safeJsonParse = (key) => {
        try {
          const item = window.sessionStorage.getItem(key);
          if (
            item === null ||
            item === undefined ||
            item === "undefined" ||
            item === ""
          )
            return null;
          return JSON.parse(item);
        } catch (error) {
          console.warn(`Error parsing ${key} from sessionStorage:`, error);
          return null;
        }
      };

      // Read all values from sessionStorage and update state
      const fileLoadDataSession = safeJsonParse("fileLoadData");
      const uploadedFilesSession = safeJsonParse("uploadedFiles");
      const fileIDSession = safeJsonParse("fileID");
      const modelDataSession = safeJsonParse("modelData");
      const warningsSession = safeJsonParse("warnings");
      const newWarningCountSession = safeJsonParse("newWarningCount");

      // Update state with what's actually stored - this will remove unsaved data
      setFileLoadData(fileLoadDataSession || []);
      setUploadedFiles(uploadedFilesSession || []);
      if (fileIDSession !== null) setFileID(fileIDSession);
      setModelData(modelDataSession || []);
      setWarnings(warningsSession || []);
      if (newWarningCountSession !== null)
        setNewWarningCount(newWarningCountSession);

      console.log(
        "Dashboard state synced with sessionStorage - unsaved data removed"
      );
    } catch (error) {
      console.error("Error syncing with sessionStorage:", error);
    }
  };

  // Function to immediately clean up unsaved data when a save fails
  const cleanupUnsavedData = () => {
    syncWithSessionStorage();
  };

  useEffect(() => {
    // Helper function to safely parse JSON from sessionStorage
    const safeJsonParse = (key) => {
      try {
        const item = window.sessionStorage.getItem(key);
        if (
          item === null ||
          item === undefined ||
          item === "undefined" ||
          item === ""
        )
          return null;
        return JSON.parse(item);
      } catch (error) {
        console.warn(`Error parsing ${key} from sessionStorage:`, error);
        return null;
      }
    };

    // Use the safe parsing function for all items
    const fileLoadDataSession = safeJsonParse("fileLoadData");
    const zeroCutParamsSession = safeJsonParse("zeroCutParams");
    const defaultXAxisUnitSession = safeJsonParse("defaultXAxisUnit");
    const otherParamsSession = safeJsonParse("otherParams");
    const modelDataSession = safeJsonParse("modelData");
    const modelNextIDSession = safeJsonParse("modelNextID");
    const minDataRangeValSession = safeJsonParse("minDataRangeVal");
    const maxDataRangeValSession = safeJsonParse("maxDataRangeVal");
    const abbrDicSession = safeJsonParse("abbrDic");
    const namingDicSession = safeJsonParse("namingDic");
    const warningsSession = safeJsonParse("warnings");
    const allLocalModelsSession = safeJsonParse("allLocalModels");
    const allLocalLineshapesSession = safeJsonParse("allLocalLineshapes");
    const valueGroupsSession = safeJsonParse("valueGroups");
    const uploadedFilesSession = safeJsonParse("uploadedFiles");
    // const graphsSession = safeJsonParse("graphs");
    const fileIDSession = safeJsonParse("fileID");
    const newWarningCountSession = safeJsonParse("newWarningCount");
    const filePresetsLoadingOptionsSession = safeJsonParse("filePresetsLoadingOptions");
    const selectedPresetLoadingOptionsSession = safeJsonParse("selectedPresetLoadingOptions");
    const maxIterationSession = safeJsonParse("maxIteration");
    const iterationRefreshCountSession = safeJsonParse("iterationRefreshCount");
    const maxRunTimeSession = safeJsonParse("maxRunTime");
    const chiValSession = safeJsonParse("chiVal");
    const totalActiveParamsSession = safeJsonParse("totalActiveParams");
    const gaugeDictSession = safeJsonParse("gaugeDict");
    const trackedParametersSession = safeJsonParse("trackedParameters");
    const subModelColTitlesSession = safeJsonParse("subModelColTitles");
    // paramWindowPosAndSize, setParamWindowPosAndSize
    const paramWindowPosAndSizeSession = safeJsonParse("paramWindowPosAndSize");
    const modelsZIndicesSession = safeJsonParse("modelsZIndices");
    const areParamsSortedSession = safeJsonParse("areParamsSorted");
    const listOfLoopModelsToSaveSession = safeJsonParse("listOfLoopModelsToSave");
    const listOfLoopCurvesToSaveSession = safeJsonParse("listOfLoopCurvesToSave");

    if (fileLoadDataSession != null) {
      setFileLoadData(fileLoadDataSession);
    }
    if (zeroCutParamsSession != null) {
      setZeroCutParams(zeroCutParamsSession);
    }
    if (defaultXAxisUnitSession != null) {
      setDefaultXAxisUnit(defaultXAxisUnitSession);
    }
    if (otherParamsSession != null) {
      setOtherParams(otherParamsSession);
    }
    if (modelDataSession != null) {
      setModelData(modelDataSession);
    }
    if (modelNextIDSession != null) {
      setModelNextID(modelNextIDSession);
    }
    if (minDataRangeValSession != null) {
      setMinDataRangeVal(minDataRangeValSession);
    }
    if (maxDataRangeValSession != null) {
      setMaxDataRangeVal(maxDataRangeValSession);
    }
    if (abbrDicSession != null) {
      setAbbrDic(abbrDicSession);
    }
    if (namingDicSession != null) {
      setNamingDic(namingDicSession);
    }
    if (warningsSession != null) {
      setWarnings(warningsSession);
    }
    if (allLocalModelsSession != null) {
      setAllLocalModels(allLocalModelsSession);
    }
    if (allLocalLineshapesSession != null) {
      setAllLocalLineshapes(allLocalLineshapesSession);
    }
    if (valueGroupsSession != null) {
      setValueGroups(valueGroupsSession);
    }
    if (uploadedFilesSession != null) {
      setUploadedFiles(uploadedFilesSession);
    }
    // if (graphsSession != null) {
    //   setGraphs(graphsSession);
    // }
    if (fileIDSession != null) {
      setFileID(fileIDSession);
    }
    if (newWarningCountSession != null) {
      setNewWarningCount(newWarningCountSession);
    }
    if (filePresetsLoadingOptionsSession != null) {
      setfilePresetsLoadingOptions(filePresetsLoadingOptionsSession);
    }
    if (selectedPresetLoadingOptionsSession != null) {
      setselectedPresetLoadingOptions(selectedPresetLoadingOptionsSession);
    }
    if (maxIterationSession != null) {
      setMaxIteration(maxIterationSession);
    }
    if (iterationRefreshCountSession != null) {
      setIterationRefreshCount(iterationRefreshCountSession);
    }
    if (maxRunTimeSession != null) {
      setMaxRunTime(maxRunTimeSession);
    }
    if (chiValSession != null) {
      setChiVal(chiValSession);
    }
    if (totalActiveParamsSession != null) {
      setTotalActiveParams(totalActiveParamsSession);
    }
    if (gaugeDictSession != null) {
      setGaugeDict(gaugeDictSession);
    }
    if (trackedParametersSession != null) {
      setTrackedParameters(trackedParametersSession);
    }
    if (subModelColTitlesSession != null) {
      setSubModelColTitles(subModelColTitlesSession);
    } 
    if (paramWindowPosAndSizeSession != null) {
      setParamWindowPosAndSize(paramWindowPosAndSizeSession);
    }
    if (modelsZIndicesSession != null) {
      setModelsZIndices(modelsZIndicesSession);
    }
    if (areParamsSortedSession != null) {
      setAreParamsSorted(areParamsSortedSession);
    }
    if (listOfLoopModelsToSaveSession != null) {
      setListOfLoopModelsToSave(listOfLoopModelsToSaveSession);
    }
    if (listOfLoopCurvesToSaveSession != null) {
      setListOfLoopCurvesToSave(listOfLoopCurvesToSaveSession);
    }
  }, []);

  function getDashboardVariables() {
    return {
      fileLoadData,
      zeroCutParams,
      defaultXAxisUnit,
      otherParams,
      modelData,
      modelNextID,
      minDataRangeVal,
      maxDataRangeVal,
      abbrDic,
      namingDic,
      warnings,
      allLocalModels,
      allLocalLineshapes,
      valueGroups,
      uploadedFiles,
      fileID,
      newWarningCount,
      filePresetsLoadingOptions,
      selectedPresetLoadingOptions,
      maxIteration,
      iterationRefreshCount,
      maxRunTime,
      chiVal,
      totalActiveParams,
      gaugeDict,
      trackedParameters,
      subModelColTitles,
      paramWindowPosAndSize,
      areParamsSorted,
      listOfLoopModelsToSave,
      listOfLoopCurvesToSave,
    };
  }

  const value = {
    resetDashboardContext,
    fileLoadData,
    setFileLoadData,
    zeroCutParams,
    setZeroCutParams,
    defaultXAxisUnit,
    setDefaultXAxisUnit,
    otherParams,
    setOtherParams,
    modelData,
    setModelData,
    modelNextID,
    setModelNextID,
    minDataRangeVal,
    setMinDataRangeVal,
    maxDataRangeVal,
    setMaxDataRangeVal,
    abbrDic,
    setAbbrDic,
    namingDic,
    setNamingDic,
    warnings,
    setWarnings,
    allLocalModels,
    setAllLocalModels,
    allLocalLineshapes,
    setAllLocalLineshapes,
    valueGroups,
    setValueGroups,
    uploadedFiles,
    setUploadedFiles,
    fileID: fileID,
    setFileID: setFileID,
    newWarningCount,
    setNewWarningCount,
    undoModels,
    setUndoModels,
    button1Active,
    setButton1Active,
    button2Active,
    setButton2Active,
    button3Active,
    setButton3Active,
    sectionsStatus,
    setSectionsStatus,
    showLeftPanel,
    setShowLeftPanel,
    callFromGraph,
    setCallFromGraph,
    fileCallFromGraph,
    setFileCallFromGraph,
    filePresetsLoadingOptions,
    setfilePresetsLoadingOptions,
    selectedPresetLoadingOptions,
    setselectedPresetLoadingOptions,
    fileToChange,
    setFileToChange,
    getDashboardVariables,
    setDashboardVariables,
    expandedWindows,
    setExpandedWindows,
    fitIteration,
    setFitIteration,
    maxIteration,
    setMaxIteration,
    iterationRefreshCount,
    setIterationRefreshCount,
    maxRunTime,
    setMaxRunTime,
    chiVal,
    setChiVal,
    totalActiveParams,
    setTotalActiveParams,
    demoOpen,
    setDemoOpen,
    tutorialOpen,
    setTutorialOpen,

    dataOpen,
    setDataOpen,
    modelsOpen,
    setModelsOpen,
    parametersOpen,
    setParametersOpen,
    fitOpen,
    setFitOpen,
    resultsOpen,
    setResultsOpen,
    chi2ReqAllowed,
    setChi2ReqAllowed,
    chi2QueRef,
    gaugeDict,
    setGaugeDict,
    addGraphModalOpen,
    setAddGraphModalOpen,
    loopOpen,
    setLoopOpen,
    outputsOpen,
    setOutputsOpen,
    loopModelFilesCustomName,
    setLoopModelFilesCustomName,
    loopCurveFilesCustomName,
    setLoopCurveFilesCustomName,
    loopIteration,
    setLoopIteration,
    listOfLoopModelsToSave,
    setListOfLoopModelsToSave,
    listOfLoopCurvesToSave,
    setListOfLoopCurvesToSave,
    trackedParameters,
    setTrackedParameters,
    pauseOnNextLoop,
    setPauseOnNextLoop,
    loopPaused,
    setLoopPaused,
    fitUndoAvailable,
    setFitUndoAvailable,
    stickyParamPosition,
    setStickyParamPosition,
    subModelColTitles,
    setSubModelColTitles,
    addSubModTitle,
    checkIfSubModTitle,
    paramWindowPosAndSize,
    setParamWindowPosAndSize,
    areParamsSorted,
    setAreParamsSorted,
    modelsZIndices,
    setModelsZIndices,
    checkParamPositions,
    setCheckParamPositions,
    syncWithSessionStorage,
    // requestedVdf,
    // setRequestedVdf,
    cleanupUnsavedData,
  };

  useEffect(() => {
    // Track which variables failed to save
    const saveResults = {
      fileLoadData: safeSessionStorageSet(
        "fileLoadData",
        JSON.stringify(fileLoadData),
        cleanupUnsavedData,
        setWarnings,
        setNewWarningCount
      ),
      zeroCutParams: safeSessionStorageSet(
        "zeroCutParams",
        JSON.stringify(zeroCutParams)
      ),
      defaultXAxisUnit: safeSessionStorageSet("defaultXAxisUnit", JSON.stringify(defaultXAxisUnit)),
      otherParams: safeSessionStorageSet(
        "otherParams",
        JSON.stringify(otherParams)
      ),
      modelData: safeSessionStorageSet(
        "modelData",
        JSON.stringify(modelData),
        cleanupUnsavedData,
        setWarnings,
        setNewWarningCount
      ),
      modelNextID: safeSessionStorageSet(
        "modelNextID",
        JSON.stringify(modelNextID)
      ),
      minDataRangeVal: safeSessionStorageSet(
        "minDataRangeVal",
        JSON.stringify(minDataRangeVal)
      ),
      maxDataRangeVal: safeSessionStorageSet(
        "maxDataRangeVal",
        JSON.stringify(maxDataRangeVal)
      ),
      abbrDic: safeSessionStorageSet("abbrDic", JSON.stringify(abbrDic)),
      namingDic: safeSessionStorageSet("namingDic", JSON.stringify(namingDic)),
      warnings: safeSessionStorageSet("warnings", JSON.stringify(warnings)),
      allLocalModels: safeSessionStorageSet(
        "allLocalModels",
        JSON.stringify(allLocalModels)
      ),
      allLocalLineshapes: safeSessionStorageSet(
        "allLocalLineshapes",
        JSON.stringify(allLocalLineshapes)
      ),
      valueGroups: safeSessionStorageSet(
        "valueGroups",
        JSON.stringify(valueGroups)
      ),
      uploadedFiles: safeSessionStorageSet(
        "uploadedFiles",
        JSON.stringify(uploadedFiles),
        cleanupUnsavedData,
        setWarnings,
        setNewWarningCount
      ),
      fileID: safeSessionStorageSet("fileID", JSON.stringify(fileID)),
      newWarningCount: safeSessionStorageSet(
        "newWarningCount",
        JSON.stringify(newWarningCount)
      ),
      filePresetsLoadingOptions: safeSessionStorageSet(
        "filePresetsLoadingOptions",
        JSON.stringify(filePresetsLoadingOptions)
      ),
      selectedPresetLoadingOptions: safeSessionStorageSet(
        "selectedPresetLoadingOptions",
        JSON.stringify(selectedPresetLoadingOptions)
      ),
      maxIteration: safeSessionStorageSet(
        "maxIteration",
        JSON.stringify(maxIteration)
      ),
      iterationRefreshCount: safeSessionStorageSet(
        "iterationRefreshCount",
        JSON.stringify(iterationRefreshCount)
      ),
      maxRunTime: safeSessionStorageSet(
        "maxRunTime",
        JSON.stringify(maxRunTime)
      ),
      chiVal: safeSessionStorageSet("chiVal", JSON.stringify(chiVal)),
      totalActiveParams: safeSessionStorageSet(
        "totalActiveParams",
        JSON.stringify(totalActiveParams)
      ),
      gaugeDict: safeSessionStorageSet("gaugeDict", JSON.stringify(gaugeDict)),
      trackedParameters: safeSessionStorageSet(
        "trackedParameters",
        JSON.stringify(trackedParameters)
      ),
      subModelColTitles: safeSessionStorageSet(
        "subModelColTitles",
        JSON.stringify(subModelColTitles)
      ),
      paramWindowPosAndSize: safeSessionStorageSet(
        "paramWindowPosAndSize",
        JSON.stringify(paramWindowPosAndSize)
      ),
      modelsZIndices: safeSessionStorageSet(
        "modelsZIndices",
        JSON.stringify(modelsZIndices)
      ),
      listOfLoopModelsToSave: safeSessionStorageSet(
        "listOfLoopModelsToSave",
        JSON.stringify(listOfLoopModelsToSave)
      ),
      listOfLoopCurvesToSave: safeSessionStorageSet(
        "listOfLoopCurvesToSave",
        JSON.stringify(listOfLoopCurvesToSave)
      ),
    };

    // Log any failed saves for debugging
    const failedSaves = Object.entries(saveResults).filter(
      ([, success]) => !success
    );
    if (failedSaves.length > 0) {
      console.warn(
        "Failed to save to sessionStorage:",
        failedSaves.map(([key]) => key)
      );
    }
  }, [value]);

  return (
    <DashboardContext.Provider value={value}>
      {props.children}
    </DashboardContext.Provider>
  );
};

export default DashboardContextProvider;
